import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import {
  usePublishStoryMap,
  useStoryMap,
  useStoryMapPublishStatus,
  useUnpublishStoryMap,
  useUpdateStoryMap,
} from "../../api/storyMaps";
import {
  StoryContent,
  StoryMapStatus,
  StoryMapVisibility,
} from "../../types/storyMap";
import { usePublishAssets } from "./print/usePublishAssets";

export interface ShareDialogStory {
  id: string;
  title: string;
  status: StoryMapStatus;
  visibility: StoryMapVisibility;
  slug: string | null;
  comments_enabled?: boolean;
}

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  story: ShareDialogStory;
  /** Byline fragment for the PDF: "X project", "X folder" or "All projects". */
  scopeLabel?: string | null;
}

/** Escape a value being interpolated into an HTML attribute in the embed snippet. */
const escapeAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export default function ShareDialog({
  open,
  onClose,
  story,
  scopeLabel,
}: ShareDialogProps) {
  const [visibility, setVisibility] = useState<StoryMapVisibility>(story.visibility);
  const [commentsEnabled, setCommentsEnabled] = useState(!!story.comments_enabled);
  const [copied, setCopied] = useState<"link" | "embed" | null>(null);
  // Slug can arrive from the publish response before the list refetches.
  const [slug, setSlug] = useState<string | null>(story.slug);
  const [status, setStatus] = useState<StoryMapStatus>(story.status);
  const [publishing, setPublishing] = useState(false);
  const [assetsWarning, setAssetsWarning] = useState(false);
  const [publishedContent, setPublishedContent] = useState<StoryContent | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const publish = usePublishStoryMap();
  const unpublish = useUnpublishStoryMap();
  const updateStory = useUpdateStoryMap();

  // Publishing runs as a backend task (it loads whole ensemble artifacts out
  // of S3), so POST /publish returns 202 and the row is polled to PUBLISHED.
  const publishStatus = useStoryMapPublishStatus(story.id, publishing);
  const publishRow = publishStatus.data?.data;
  // The stored row, so a story published earlier (or in another tab) can have
  // its document prepared without re-running the freeze.
  const storyRow = useStoryMap(open ? story.id : undefined);
  const storedPdfUrl = pdfUrl ?? storyRow.data?.data.published_pdf_url ?? null;

  useEffect(() => {
    if (open) {
      setVisibility(story.visibility);
      setCommentsEnabled(!!story.comments_enabled);
      setSlug(story.slug);
      setStatus(story.status);
      setCopied(null);
      setPublishing(story.status === "PUBLISHING");
      setAssetsWarning(false);
      setPdfUrl(null);
      publish.reset();
      unpublish.reset();
      updateStory.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, story.id]);

  // Follow the polled row until the freeze lands.
  useEffect(() => {
    if (!publishRow) return;
    setStatus(publishRow.status);
    if (publishRow.slug) setSlug(publishRow.slug);
    if (publishRow.status === "PUBLISHED") {
      setPublishedContent(publishRow.published_content);
      setPublishedAt(publishRow.published_at);
      setPdfUrl(publishRow.published_pdf_url ?? null);
      setPublishing(false);
    } else if (publishRow.status === "PUBLISH_FAILED" || publishRow.status === "DRAFT") {
      setPublishing(false);
    }
  }, [publishRow]);

  // Render the PDF and the per-scene stills once the freeze is done. Runs here
  // because the pipeline is WebGL and this tab already holds every map.
  const assets = usePublishAssets({
    storyMapId: story.id,
    storyTitle: story.title,
    content: publishedContent,
    scopeLabel,
    publishedAt,
    enabled: !!publishedContent && status === "PUBLISHED",
    onFinished: ({ ok }) => {
      if (!ok) setAssetsWarning(true);
      setPublishedContent(null);
    },
  });
  const buildingAssets = assets.phase !== "idle" && assets.phase !== "done" &&
    assets.phase !== "failed";

  const published = status === "PUBLISHED";
  const shareUrl = slug ? `${window.location.origin}/s/${slug}` : null;
  // Embeds get their own route rather than the page sniffing `window.top`:
  // frame detection misfires on anyone framing the page for a legitimate
  // reason and can be defeated by whoever is embedding you, and it makes the
  // billing behaviour invisible from the server. The URL is generated here, so
  // it is the one thing we do control.
  const embedUrl = slug ? `${window.location.origin}/s/${slug}/embed` : null;
  const embedSnippet =
    embedUrl && `<iframe src="${escapeAttribute(embedUrl)}" width="100%" height="600" style="border:0" title="${escapeAttribute(story.title)}"></iframe>`;

  const settingsChanged =
    visibility !== story.visibility || commentsEnabled !== !!story.comments_enabled;

  const copy = (text: string, which: "link" | "embed") => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handlePublish = () => {
    setAssetsWarning(false);
    publish.mutate(
      { storyMapId: story.id, visibility, commentsEnabled },
      {
        onSuccess: (response) => {
          setStatus(response.data.status);
          if (response.data.slug) setSlug(response.data.slug);
          setPublishing(true);
        },
      }
    );
  };

  /**
   * Changing who can see an already-published story, or whether readers may
   * comment, is a PATCH, not a re-publish: a re-publish re-runs the whole
   * freeze — every animation's frames re-read from S3 and re-uploaded, and
   * sim_kpi metrics re-resolved, which can legitimately change numbers a
   * reader has already been shown.
   */
  const handleSettingsOnly = () => {
    updateStory.mutate(
      { storyMapId: story.id, data: { visibility, comments_enabled: commentsEnabled } },
      {
        onSuccess: (response) => {
          setVisibility(response.data.visibility);
          setCommentsEnabled(!!response.data.comments_enabled);
        },
      }
    );
  };

  /**
   * Render the document for a story that is already published but has no
   * stored PDF — an interrupted publish, a tab closed mid-render, or a story
   * published before this existed. Uses the FROZEN content, so it never
   * re-runs the freeze and can't change numbers a reader has already seen.
   */
  const handlePrepareDocument = () => {
    const frozen = storyRow.data?.data;
    if (!frozen?.published_content) return;
    setAssetsWarning(false);
    setPublishedAt(frozen.published_at);
    setPublishedContent(frozen.published_content);
  };

  const handleUnpublish = () => {
    unpublish.mutate(story.id, {
      onSuccess: (response) => setStatus(response.data.status),
    });
  };

  const busy = publish.isPending || publishing || buildingAssets;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: "#212b37", color: "white" }}>
        Share “{story.title}”
      </DialogTitle>
      <DialogContent sx={{ pt: "16px !important" }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Who can view
        </Typography>
        <RadioGroup
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as StoryMapVisibility)}
        >
          <FormControlLabel
            value="PUBLIC"
            control={<Radio color="secondary" />}
            label="Anyone with the link"
          />
          <FormControlLabel
            value="ORG"
            control={<Radio color="secondary" />}
            label="Your organisation only"
          />
        </RadioGroup>

        <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
          Comments
        </Typography>
        <FormControlLabel
          control={
            <Switch
              color="secondary"
              checked={commentsEnabled}
              onChange={(e) => setCommentsEnabled(e.target.checked)}
            />
          }
          label="Let readers comment on scenes"
        />
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: -0.5 }}>
          Readers can open a thread on any scene, reply and resolve. Their
          comments appear on the matching scene in the builder. Turning this
          off later hides the comments without deleting them.
        </Typography>
        {published && settingsChanged && (
          <Button
            size="small"
            onClick={handleSettingsOnly}
            disabled={updateStory.isPending}
            sx={{ textTransform: "none", mt: 1 }}
          >
            {updateStory.isPending ? "Updating…" : "Update sharing settings"}
          </Button>
        )}

        {busy && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress sx={{ mb: 1 }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {buildingAssets
                ? assets.detail || "Preparing the published document…"
                : publishRow?.publish_detail || "Freezing the story…"}
            </Typography>
            {assets.phase === "capturing" && (
              <Button size="small" onClick={assets.skip} sx={{ textTransform: "none" }}>
                Skip the remaining maps
              </Button>
            )}
          </Box>
        )}

        {status === "PUBLISH_FAILED" && !busy && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Publishing failed. {publishRow?.publish_error || "Please try again."}
          </Alert>
        )}

        {published && shareUrl && !busy && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
              Public link
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                value={shareUrl}
                InputProps={{ readOnly: true }}
              />
              <Tooltip title={copied === "link" ? "Copied!" : "Copy link"} arrow>
                <IconButton onClick={() => copy(shareUrl, "link")}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
              Embed
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={embedSnippet ?? ""}
                InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
              />
              <Tooltip title={copied === "embed" ? "Copied!" : "Copy embed code"} arrow>
                <IconButton onClick={() => embedSnippet && copy(embedSnippet, "embed")}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Embedded stories show the map as images. Readers who open the link
              itself get the full, explorable maps.
            </Typography>

            <Button
              size="small"
              startIcon={<PictureAsPdfIcon />}
              sx={{ mt: 1.5, textTransform: "none", display: "block" }}
              onClick={() =>
                window.open(storedPdfUrl ?? `${shareUrl}/print`, "_blank", "noopener")
              }
            >
              {storedPdfUrl ? "Download PDF" : "Open PDF preview"}
            </Button>
            {!storedPdfUrl && storyRow.data?.data.published_content && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  This story has no prepared document, so readers rebuild it in
                  their own browser — slow for them, and it re-renders every map
                  each time.
                </Typography>
                <Button
                  size="small"
                  onClick={handlePrepareDocument}
                  sx={{ textTransform: "none" }}
                >
                  Prepare the PDF and embed images
                </Button>
              </Box>
            )}
          </>
        )}

        <Alert
          icon={<AcUnitIcon fontSize="small" />}
          severity="info"
          sx={{ mt: 2 }}
        >
          Publishing freezes all map scenes, charts and metrics as of now.
          Re-publish anytime to refresh.
        </Alert>

        {published && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Unpublishing hides the story from this link, but anything already
            shared — frozen map data, images and the PDF — stays reachable to
            anyone holding those URLs. Delete the story to remove them.
          </Alert>
        )}

        {assetsWarning && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            The story is published, but its PDF and embed images couldn't be
            prepared. Readers will still get everything — their browser just
            builds the document itself. Re-publish to try again.
          </Alert>
        )}

        {(publish.isError || unpublish.isError || updateStory.isError) && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {publish.error?.message ||
              unpublish.error?.message ||
              updateStory.error?.message ||
              "Request failed"}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {published && (
          <Button
            onClick={handleUnpublish}
            disabled={unpublish.isPending || busy}
            sx={{ mr: "auto", textTransform: "none", color: "text.secondary" }}
          >
            Unpublish
          </Button>
        )}
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
          sx={{
            textTransform: "none",
            bgcolor: "#00d4ad",
            color: "#212b37",
            fontWeight: 600,
            "&:hover": { bgcolor: "#00bd9a" },
          }}
        >
          {busy ? "Publishing…" : published ? "Re-publish" : "Publish"}
        </Button>
      </DialogActions>
      {/* The hidden capture map — must stay mounted while it runs. */}
      {assets.runner}
    </Dialog>
  );
}
